import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  Pressable,
  ActivityIndicator,
  StyleSheet,
  Platform,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatFundsCents, MAIN_MUTUAL_FUND_ID } from '../../constants/userFunds';
import { grossUpCardChargeCents } from '../../utils/stripeCardFees';
import { palette, FONT } from '../../styles/tourTheme';

const STEPS = {
  AMOUNT: 1,
  SPLIT: 2,
  CONFIRM: 3,
};

function parseDollarsToCents(raw) {
  const n = Number(String(raw || '').replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

/**
 * Step-by-step mutual fund donation:
 * 1) Enter total amount
 * 2) Choose Wallet vs card (only when balance > 0)
 * 3) Confirm & donate
 */
export default function MutualFundDonateWizardModal({
  visible,
  onClose,
  fund = null,
  availableCents = 0,
  busy = false,
  isDarkMode = true,
  accent,
  successUrl,
  cancelUrl,
  /**
   * (payload) => Promise
   * payload: { fundId, creditCents, cardCents, successUrl?, cancelUrl? }
   */
  onDonate,
}) {
  const pal = palette(isDarkMode);
  const hue = accent || pal.teal;
  const styles = useMemo(() => makeStyles(pal, hue), [pal, hue]);

  const [step, setStep] = useState(STEPS.AMOUNT);
  const [totalInput, setTotalInput] = useState('');
  const [creditInput, setCreditInput] = useState('');
  const [error, setError] = useState('');

  const fundTitle = fund?.title || 'Mutual Fund';
  const isMain = fund?.isMain || fund?.id === MAIN_MUTUAL_FUND_ID;
  const available = Math.max(0, Math.round(Number(availableCents) || 0));
  const hasBalance = available >= 1;
  const stepCount = hasBalance ? 3 : 2;

  useEffect(() => {
    if (!visible) return;
    setStep(STEPS.AMOUNT);
    setTotalInput('');
    setCreditInput(hasBalance ? '' : '0');
    setError('');
  }, [visible, fund?.id, hasBalance]);

  const totalCents = parseDollarsToCents(totalInput) || 0;

  const creditCents = useMemo(() => {
    if (!hasBalance || totalCents <= 0) return 0;
    const maxCredit = Math.min(available, totalCents);
    if (!String(creditInput).trim()) return maxCredit;
    const typed = parseDollarsToCents(creditInput);
    if (typed == null) return 0;
    return Math.min(typed, maxCredit);
  }, [hasBalance, totalCents, available, creditInput]);

  const cardCents = Math.max(0, totalCents - creditCents);

  const cardCheckoutPreview = useMemo(() => {
    const net = hasBalance ? cardCents : totalCents;
    if (net < 100) return null;
    return grossUpCardChargeCents(net);
  }, [hasBalance, cardCents, totalCents]);

  const splitError = useMemo(() => {
    if (totalCents <= 0) return '';
    if (cardCents > 0 && cardCents < 100) {
      return hasBalance
        ? 'Card portion must be at least $1.00 — use more balance or raise the total.'
        : 'Minimum card donation is $1.00.';
    }
    if (creditCents > available) return 'Not enough Wallet balance.';
    return '';
  }, [totalCents, cardCents, creditCents, available, hasBalance]);

  const goAmountNext = () => {
    setError('');
    const cents = parseDollarsToCents(totalInput);
    if (!cents) {
      setError('Enter how much you want to donate.');
      return;
    }
    if (!hasBalance) {
      if (cents < 100) {
        setError('Minimum card donation is $1.00.');
        return;
      }
      setCreditInput('0');
      setStep(STEPS.CONFIRM);
      return;
    }
    setCreditInput((Math.min(available, cents) / 100).toFixed(2));
    setStep(STEPS.SPLIT);
  };

  const goSplitNext = () => {
    setError('');
    if (splitError) {
      setError(splitError);
      return;
    }
    if (totalCents < 1) {
      setError('Enter a donation amount.');
      return;
    }
    setStep(STEPS.CONFIRM);
  };

  const setCreditsOnly = () => {
    const max = Math.min(available, totalCents);
    setCreditInput((max / 100).toFixed(2));
  };

  const setCardOnly = () => {
    setCreditInput('0');
  };

  const adjustCredits = (deltaCents) => {
    const next = Math.max(0, Math.min(Math.min(available, totalCents), creditCents + deltaCents));
    setCreditInput((next / 100).toFixed(2));
  };

  const handleConfirm = async () => {
    setError('');
    if (!fund?.id) {
      setError('No fund selected.');
      return;
    }
    if (splitError) {
      setError(splitError);
      return;
    }
    try {
      await onDonate?.({
        fundId: fund.id,
        creditCents: hasBalance ? creditCents : 0,
        cardCents: hasBalance ? cardCents : totalCents,
        successUrl,
        cancelUrl,
      });
    } catch (err) {
      setError(err?.message || 'Donation failed');
    }
  };

  const stepLabel =
    step === STEPS.AMOUNT
      ? `Step 1 of ${stepCount} · Amount`
      : step === STEPS.SPLIT
        ? `Step 2 of ${stepCount} · Wallet & card`
        : `Step ${stepCount} of ${stepCount} · Confirm`;

  const progressSteps = hasBalance ? [1, 2, 3] : [1, 3];
  const progressActive =
    step === STEPS.AMOUNT ? 1 : step === STEPS.SPLIT ? 2 : 3;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={busy ? undefined : onClose}
    >
      <Pressable style={styles.backdrop} onPress={busy ? undefined : onClose}>
        <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation?.()}>
          <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingBottom: 4 }}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.header}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.kicker}>{stepLabel}</Text>
                <Text style={styles.title} numberOfLines={2}>
                  Donate
                </Text>
                <Text style={styles.fundLine} numberOfLines={1}>
                  {isMain ? 'MAIN · ' : ''}
                  {fundTitle}
                </Text>
              </View>
              <TouchableOpacity
                onPress={onClose}
                disabled={busy}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityLabel="Close"
                style={[styles.closeBtn, { borderColor: pal.line }]}
              >
                <Ionicons name="close" size={18} color={pal.ink} />
              </TouchableOpacity>
            </View>

            <View style={styles.progressRow}>
              {progressSteps.map((n) => (
                <View
                  key={n}
                  style={[
                    styles.progressSeg,
                    { backgroundColor: pal.lineSoft },
                    progressActive >= n && { backgroundColor: hue },
                  ]}
                />
              ))}
            </View>

            {error ? (
              <View style={[styles.banner, { backgroundColor: pal.alert }]}>
                <Text style={styles.bannerText}>{error}</Text>
              </View>
            ) : null}

            {step === STEPS.AMOUNT ? (
              <View style={styles.body}>
                <Text style={styles.hint}>How much do you want to donate?</Text>
                <View style={[styles.inputRow, { borderColor: pal.line, backgroundColor: pal.sunk }]}>
                  <Text style={[styles.dollar, { color: pal.ink }]}>$</Text>
                  <TextInput
                    style={[styles.input, { color: pal.ink }]}
                    value={totalInput}
                    onChangeText={setTotalInput}
                    placeholder="25.00"
                    placeholderTextColor={pal.faint}
                    keyboardType="decimal-pad"
                    autoFocus={Platform.OS === 'web'}
                  />
                </View>
                <View style={styles.presetRow}>
                  {['5', '10', '25', '50'].map((p) => {
                    const on = totalInput === p;
                    return (
                      <TouchableOpacity
                        key={p}
                        style={[
                          styles.chip,
                          {
                            borderColor: on ? pal.ink : pal.line,
                            backgroundColor: on ? pal.ink : 'transparent',
                          },
                        ]}
                        onPress={() => setTotalInput(p)}
                      >
                        <Text style={[styles.chipText, { color: on ? pal.page : pal.mute }]}>
                          ${p}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                {hasBalance ? (
                  <View style={[styles.statCard, { borderColor: pal.line, backgroundColor: pal.surf }]}>
                    <Text style={[styles.kicker, { color: pal.mute, marginBottom: 4 }]}>
                      Wallet available
                    </Text>
                    <Text style={[styles.statValue, { color: pal.ink }]}>
                      {formatFundsCents(available)}
                    </Text>
                    <Text style={[styles.meta, { color: pal.mute, marginTop: 6 }]}>
                      You can apply this on the next step, pay by card, or both.
                    </Text>
                  </View>
                ) : (
                  <Text style={[styles.meta, { color: pal.mute }]}>
                    Paid by card via Stripe Checkout.
                  </Text>
                )}
                <TouchableOpacity
                  style={[styles.primary, { backgroundColor: hue }]}
                  onPress={goAmountNext}
                >
                  <Text style={styles.primaryText}>Continue</Text>
                </TouchableOpacity>
              </View>
            ) : null}

            {step === STEPS.SPLIT && hasBalance ? (
              <View style={styles.body}>
                <Text style={styles.hint}>
                  Split {formatFundsCents(totalCents)} between your Wallet and a card payment.
                </Text>

                <View style={styles.presetRow}>
                  <TouchableOpacity
                    style={[styles.chip, { borderColor: pal.line, flex: 1 }]}
                    onPress={setCreditsOnly}
                    disabled={available < 1}
                  >
                    <Text style={[styles.chipText, { color: pal.mute }]}>All from balance</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.chip, { borderColor: pal.line, flex: 1 }]}
                    onPress={setCardOnly}
                  >
                    <Text style={[styles.chipText, { color: pal.mute }]}>All card</Text>
                  </TouchableOpacity>
                </View>

                <Text style={[styles.kicker, { color: pal.mute }]}>From Wallet</Text>
                <Text style={[styles.meta, { color: pal.mute, marginBottom: 8, marginTop: 4 }]}>
                  Available: {formatFundsCents(available)}
                  {available < 1 ? ' — nothing to apply' : ''}
                </Text>
                <View style={[styles.inputRow, { borderColor: pal.line, backgroundColor: pal.sunk }]}>
                  <Text style={[styles.dollar, { color: pal.ink }]}>$</Text>
                  <TextInput
                    style={[styles.input, { color: pal.ink }]}
                    value={creditInput}
                    onChangeText={setCreditInput}
                    placeholder="0.00"
                    placeholderTextColor={pal.faint}
                    keyboardType="decimal-pad"
                  />
                </View>
                <View style={styles.presetRow}>
                  <TouchableOpacity
                    style={[styles.chip, { borderColor: pal.line }]}
                    onPress={() => adjustCredits(-100)}
                  >
                    <Text style={[styles.chipText, { color: pal.mute }]}>−$1</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.chip, { borderColor: pal.line }]}
                    onPress={() => adjustCredits(100)}
                  >
                    <Text style={[styles.chipText, { color: pal.mute }]}>+$1</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.chip, { borderColor: pal.line }]}
                    onPress={() =>
                      setCreditInput((Math.min(available, totalCents) / 100).toFixed(2))
                    }
                  >
                    <Text style={[styles.chipText, { color: pal.mute }]}>Max balance</Text>
                  </TouchableOpacity>
                </View>

                <View style={[styles.breakdown, { borderColor: pal.line, borderLeftColor: hue }]}>
                  <View style={styles.breakdownRow}>
                    <Text style={[styles.breakdownLabel, { color: pal.mute }]}>From balance</Text>
                    <Text style={[styles.breakdownValue, { color: pal.ink }]}>
                      {formatFundsCents(creditCents)}
                    </Text>
                  </View>
                  <View style={[styles.breakdownRow, { borderTopColor: pal.lineSoft }]}>
                    <Text style={[styles.breakdownLabel, { color: pal.mute }]}>By card</Text>
                    <Text style={[styles.breakdownValue, { color: pal.ink }]}>
                      {formatFundsCents(cardCents)}
                    </Text>
                  </View>
                  <View style={[styles.breakdownRow, styles.breakdownTotal, { borderTopColor: pal.line }]}>
                    <Text style={[styles.breakdownTotalLabel, { color: pal.ink }]}>Total</Text>
                    <Text style={[styles.breakdownTotalValue, { color: hue }]}>
                      {formatFundsCents(totalCents)}
                    </Text>
                  </View>
                </View>

                <View style={styles.navRow}>
                  <TouchableOpacity
                    style={[styles.secondary, { borderColor: pal.line }]}
                    onPress={() => setStep(STEPS.AMOUNT)}
                  >
                    <Text style={[styles.secondaryText, { color: pal.mute }]}>Back</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.primary, { backgroundColor: hue, flex: 1 }]}
                    onPress={goSplitNext}
                  >
                    <Text style={styles.primaryText}>Continue</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : null}

            {step === STEPS.CONFIRM ? (
              <View style={styles.body}>
                <Text style={styles.hint}>Review and confirm your donation.</Text>
                <View style={[styles.breakdown, { borderColor: pal.line, borderLeftColor: hue }]}>
                  <View style={styles.breakdownRow}>
                    <Text style={[styles.breakdownLabel, { color: pal.mute }]}>Fund</Text>
                    <Text style={[styles.breakdownValue, { color: pal.ink }]} numberOfLines={1}>
                      {fundTitle}
                    </Text>
                  </View>
                  {hasBalance && creditCents > 0 ? (
                    <View style={[styles.breakdownRow, { borderTopColor: pal.lineSoft }]}>
                      <Text style={[styles.breakdownLabel, { color: pal.mute }]}>From balance</Text>
                      <Text style={[styles.breakdownValue, { color: pal.ink }]}>
                        {formatFundsCents(creditCents)}
                      </Text>
                    </View>
                  ) : null}
                  {(cardCents > 0 || !hasBalance) ? (
                    <View style={[styles.breakdownRow, { borderTopColor: pal.lineSoft }]}>
                      <Text style={[styles.breakdownLabel, { color: pal.mute }]}>To fund (card)</Text>
                      <Text style={[styles.breakdownValue, { color: pal.ink }]}>
                        {formatFundsCents(hasBalance ? cardCents : totalCents)}
                      </Text>
                    </View>
                  ) : null}
                  {cardCheckoutPreview ? (
                    <>
                      <View style={[styles.breakdownRow, { borderTopColor: pal.lineSoft }]}>
                        <Text style={[styles.breakdownLabel, { color: pal.mute }]}>Processing fee</Text>
                        <Text style={[styles.breakdownValue, { color: pal.ink }]}>
                          {formatFundsCents(cardCheckoutPreview.processingFeeCents)}
                        </Text>
                      </View>
                      <View style={[styles.breakdownRow, { borderTopColor: pal.lineSoft }]}>
                        <Text style={[styles.breakdownLabel, { color: pal.mute }]}>Card charge</Text>
                        <Text style={[styles.breakdownValue, { color: pal.ink }]}>
                          {formatFundsCents(cardCheckoutPreview.chargeCents)}
                        </Text>
                      </View>
                    </>
                  ) : null}
                  <View style={[styles.breakdownRow, styles.breakdownTotal, { borderTopColor: pal.line }]}>
                    <Text style={[styles.breakdownTotalLabel, { color: pal.ink }]}>To fund</Text>
                    <Text style={[styles.breakdownTotalValue, { color: hue }]}>
                      {formatFundsCents(totalCents)}
                    </Text>
                  </View>
                </View>
                <Text style={[styles.meta, { color: pal.mute }]}>
                  {!hasBalance || (cardCents > 0 && creditCents <= 0)
                    ? 'You will continue to Stripe Checkout for the card payment.'
                    : cardCents > 0
                      ? 'Wallet applies now; card opens Stripe Checkout next.'
                      : 'Taken from your Wallet immediately — no card charge.'}
                </Text>
                <View style={styles.navRow}>
                  <TouchableOpacity
                    style={[styles.secondary, { borderColor: pal.line }]}
                    onPress={() => setStep(hasBalance ? STEPS.SPLIT : STEPS.AMOUNT)}
                    disabled={busy}
                  >
                    <Text style={[styles.secondaryText, { color: pal.mute }]}>Back</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.primary,
                      { backgroundColor: hue, flex: 1 },
                      busy && styles.disabled,
                    ]}
                    onPress={handleConfirm}
                    disabled={busy}
                  >
                    {busy ? (
                      <ActivityIndicator color="#fff" />
                    ) : (
                      <Text style={styles.primaryText}>
                        {(hasBalance ? cardCents : totalCents) > 0
                          ? 'Confirm & pay'
                          : 'Confirm donation'}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            ) : null}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function makeStyles(pal, hue) {
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    sheet: {
      width: '100%',
      maxWidth: 440,
      maxHeight: '92%',
      borderRadius: 12,
      backgroundColor: pal.page,
      borderWidth: 1,
      borderColor: pal.line,
      borderLeftWidth: 3,
      borderLeftColor: hue,
      padding: 20,
      ...Platform.select({
        web: { boxShadow: '0 18px 48px rgba(0,0,0,0.28)' },
        default: {},
      }),
    },
    header: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      marginBottom: 14,
      paddingBottom: 14,
      borderBottomWidth: 2,
      borderBottomColor: hue,
    },
    kicker: {
      fontFamily: FONT.mono,
      fontSize: 12,
      letterSpacing: 1.2,
      textTransform: 'uppercase',
      marginBottom: 4,
    },
    title: {
      fontFamily: FONT.sans,
      fontSize: 28,
      fontWeight: '700',
      letterSpacing: -0.5,
      color: pal.ink,
    },
    fundLine: {
      marginTop: 4,
      fontFamily: FONT.mono,
      fontSize: 12.5,
      color: pal.mute,
    },
    closeBtn: {
      width: 32,
      height: 32,
      borderRadius: 8,
      borderWidth: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    progressRow: {
      flexDirection: 'row',
      gap: 6,
      marginBottom: 16,
    },
    progressSeg: {
      flex: 1,
      height: 3,
      borderRadius: 2,
    },
    banner: {
      padding: 12,
      borderRadius: 9,
      marginBottom: 12,
    },
    bannerText: {
      color: '#fff',
      fontSize: 14,
      lineHeight: 19,
    },
    body: {
      gap: 12,
    },
    hint: {
      fontSize: 15,
      color: pal.mute,
      lineHeight: 22,
    },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderWidth: 1,
      borderRadius: 8,
      paddingHorizontal: 12,
    },
    dollar: {
      fontFamily: FONT.mono,
      fontSize: 20,
      fontWeight: '600',
    },
    input: {
      flex: 1,
      fontFamily: FONT.mono,
      fontSize: 22,
      fontWeight: '600',
      paddingVertical: 12,
      outlineStyle: 'none',
    },
    presetRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    chip: {
      borderWidth: 1,
      borderRadius: 6,
      paddingHorizontal: 10,
      paddingVertical: 7,
      alignItems: 'center',
    },
    chipText: {
      fontFamily: FONT.mono,
      fontSize: 12,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    statCard: {
      borderWidth: 1,
      borderRadius: 12,
      padding: 16,
    },
    statValue: {
      fontSize: 28,
      fontWeight: '600',
      letterSpacing: -0.5,
    },
    meta: {
      fontSize: 13.5,
      lineHeight: 19,
    },
    breakdown: {
      borderWidth: 1,
      borderLeftWidth: 3,
      borderRadius: 12,
      padding: 16,
      backgroundColor: pal.surf,
    },
    breakdownRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 12,
      paddingVertical: 8,
      borderTopWidth: 1,
      borderTopColor: 'transparent',
    },
    breakdownLabel: {
      fontSize: 15,
    },
    breakdownValue: {
      fontFamily: FONT.mono,
      fontSize: 14,
      fontWeight: '600',
      maxWidth: '58%',
      textAlign: 'right',
    },
    breakdownTotal: {
      marginTop: 4,
      paddingTop: 12,
    },
    breakdownTotalLabel: {
      fontFamily: FONT.mono,
      fontSize: 12,
      letterSpacing: 1,
      textTransform: 'uppercase',
      fontWeight: '600',
    },
    breakdownTotalValue: {
      fontFamily: FONT.mono,
      fontSize: 16,
      fontWeight: '700',
    },
    navRow: {
      flexDirection: 'row',
      gap: 10,
      marginTop: 4,
    },
    primary: {
      borderRadius: 9,
      paddingVertical: 12,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 44,
    },
    primaryText: {
      color: '#fff',
      fontWeight: '600',
      fontSize: 15,
      textAlign: 'center',
    },
    secondary: {
      borderWidth: 1,
      borderRadius: 9,
      paddingVertical: 12,
      paddingHorizontal: 14,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 44,
    },
    secondaryText: {
      fontFamily: FONT.mono,
      fontSize: 12,
      letterSpacing: 0.7,
      textTransform: 'uppercase',
    },
    disabled: { opacity: 0.55 },
  });
}
